import { Lit3formBlock, html, unsafeHTML } from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  analyticsCtaDataObject,
  analyticsLabelFromString,
} from '../../../helpers/adobe-analytics';

export class Tabby extends Lit3formBlock {
  static get characterName() {
    return 'tabby';
  }

  static get characterStyles() {
    return [...styles];
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="block-grid tabby-layout">
          ${this.headerHTML} ${this.tableHTML} ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get headerHTML() {
    if (!this.headerText && !this.descriptionText && !this.ctaData) {
      return html``;
    }
    return html`
      <block-intro
        class="intro"
        header=${this.content.header || ''}
        description=${this.content.description || ''}
      >
        ${this.ctaHTML}
      </block-intro>
    `;
  }

  get ctaHTML() {
    const cta = this.ctaData;
    if (!cta || !cta.text) return html``;
    return html`
      <cta-text
        class="learn-more"
        cta-data=${JSON.stringify(cta)}
        .ctaAnalyticsData=${analyticsCtaDataObject(cta, this.headerText)}
      ></cta-text>
    `;
  }

  get tableHTML() {
    const grid = this.gridData;
    if (!grid || !grid.headerRow.length) return html``;

    return html`
      <div class="table-scroll">
        <table class="matrix">
          <thead>
            <tr>
              ${grid.headerRow.map(
                (cell, colIdx) => html`
                  <th class=${this.headerCellClass(colIdx)}>
                    ${this.cellContent(cell)}
                  </th>
                `
              )}
            </tr>
          </thead>
          <tbody>
            ${grid.bodyRows.map(
              row => html`
                <tr>
                  ${row.map(
                    (cell, colIdx) => html`
                      <td class=${this.bodyCellClass(colIdx)}>
                        ${this.cellContent(cell)}
                      </td>
                    `
                  )}
                </tr>
              `
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  cellContent({ text, tooltip }) {
    const content = text ? unsafeHTML(text) : '';
    if (!tooltip) return content;

    return html`
      <div class="cell-with-tooltip">
        <div class="cell-text">${content}</div>
        <info-tooltip
          text=${tooltip}
          analytics-placement=${this.analyticsBlockLabel}
          analytics-label=${analyticsLabelFromString(text || tooltip)}
        ></info-tooltip>
      </div>
    `;
  }

  headerCellClass(colIdx) {
    return colIdx === 0 ? 'cell header-cell corner-cell' : 'cell header-cell';
  }

  bodyCellClass(colIdx) {
    return colIdx === 0 ? 'cell feature-cell' : 'cell body-cell';
  }

  /*
   * matrix_table shape (from MatrixTable#to_json):
   *   {
   *     corner_text: string,
   *     column_headers: [string, ...],       // ordered, N columns
   *     row_headers:    [string, ...],       // ordered, M rows
   *     cells: { "row,col": string, ... },   // sparse; missing = empty
   *     tooltips: { "row,col": string, ... } // sparse; -1 = header row/col
   *   }
   *
   * Each grid cell comes back as { text, tooltip }.
   */
  get gridData() {
    const table = this.content.matrix_table;
    if (!table) return null;

    const columnHeaders = table.column_headers || [];
    const rowHeaders = table.row_headers || [];
    const cells = table.cells || {};
    const tooltips = table.tooltips || {};
    const corner = table.corner_text || '';

    if (!columnHeaders.length || !rowHeaders.length) return null;

    const cell = (text, rowIdx, colIdx) => ({
      text,
      tooltip: tooltips[rowIdx + ',' + colIdx] || '',
    });

    return {
      headerRow: [
        cell(corner, -1, -1),
        ...columnHeaders.map((header, colIdx) => cell(header, -1, colIdx)),
      ],
      bodyRows: rowHeaders.map((rowHeader, rowIdx) => [
        cell(rowHeader, rowIdx, -1),
        ...columnHeaders.map((_, colIdx) =>
          cell(cells[rowIdx + ',' + colIdx] || '', rowIdx, colIdx)
        ),
      ]),
    };
  }

  get ctaData() {
    return this.content.call_to_action;
  }
}
Tabby.defineElement();
